"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VersionArtifactSchema = exports.AcceptanceSchema = exports.ARTIFACT_FORMATS = exports.ACCEPTANCE_METHODS = exports.PARTY_TYPES = void 0;
const zod_1 = require("zod");
/**
 * The acceptance ledger and the frozen artifacts a published version points at.
 *
 * Both records are append-only and version-scoped. An acceptance names the exact
 * `versionNo` the party saw; an artifact names the exact bytes that were served.
 * Neither is ever rewritten — `isAcceptanceStale()` in ./documentLifecycle derives
 * who owes re-acceptance from the version chain, so nothing here needs a sweep.
 */
/**
 * Everyone who can be held to a document. Spec §4.2 keys acceptances
 * `PARTY#<type>#<id>`.
 *
 * `affiliate` is a partner in the affiliate programme — a party whose Cognito
 * identity lives in the affiliates pool, not the tenant one. Added when the
 * partner agreement gate shipped; widening this list is additive, and an
 * existing acceptance row is unaffected.
 */
exports.PARTY_TYPES = ['tenant', 'staff', 'employee', 'customer', 'affiliate'];
/**
 * How consent was captured. `signature` is the envelope path (spec §6); `implied`
 * covers continued-use acceptance. All three still require evidence — see below.
 */
exports.ACCEPTANCE_METHODS = ['click', 'signature', 'implied'];
/** The two frozen outputs of a publish. React is a live view, never an artifact. */
exports.ARTIFACT_FORMATS = ['html', 'pdf'];
const hasText = (v) => typeof v === 'string' && v.trim().length > 0;
exports.AcceptanceSchema = zod_1.z.object({
    partyType: zod_1.z.enum(exports.PARTY_TYPES),
    partyId: zod_1.z.string().min(1),
    documentId: zod_1.z.string().min(1),
    /**
     * Acceptance is scoped to ONE version, never to "the document". Without this
     * number staleness is unanswerable and the publish blast radius is a guess.
     */
    versionNo: zod_1.z.number().int().positive(),
    acceptedAt: zod_1.z.string(),
    method: zod_1.z.enum(exports.ACCEPTANCE_METHODS),
    ip: zod_1.z.string().optional(),
    userAgent: zod_1.z.string().optional(),
}).refine((a) => hasText(a.ip) || hasText(a.userAgent), {
    message: 'an acceptance needs at least one of ip or userAgent — a record with no evidence is not evidence',
    path: ['ip'],
});
/**
 * A rendered, frozen file for one published version. Written once at publish and
 * never re-rendered: re-rendering risks serving a document that differs from the
 * one accepted, which is the whole ballgame in a dispute (spec D6).
 */
exports.VersionArtifactSchema = zod_1.z.object({
    format: zod_1.z.enum(exports.ARTIFACT_FORMATS),
    s3Key: zod_1.z.string().min(1),
    /**
     * LOWERCASE hex only. A case-insensitive match would let two spellings of the
     * same digest compare unequal, so the fingerprint has exactly one form.
     */
    sha256: zod_1.z.string().regex(/^[0-9a-f]{64}$/, 'sha256 must be 64 lowercase hex characters'),
    /** Stamped so any frozen artifact can be reproduced byte-for-byte later. */
    rendererVersion: zod_1.z.string().min(1),
    bytes: zod_1.z.number().int().positive(),
    renderedAt: zod_1.z.string(),
});
//# sourceMappingURL=documentAcceptance.js.map